import React from 'react';
import { Tooltip } from 'antd';
import { Button, ButtonVariants, ButtonColors } from '@clockout-dev/design-elements';
import { useScopes } from '../../../hooks/useScope';
import { describeRollup, describeSyncReport } from './syncStatus.constants';
import { formatDateTime, timeZoneAbbreviation } from '../../../utils/datetime';
import styles from './styles/sync-status-cell.module.scss';

/**
 * SYNC STATUS cell of the Users table.
 *
 * The default render needs NO request: the rollup arrives with the page as
 * `record.syncStatus` (one bulk query server-side). The per-dimension icon row
 * appears only when the user's detail report has been loaded (the row is
 * selected), so the table never fires one call per row and no row can sit in
 * a loading state.
 *
 * Three inputs, three renders:
 *  - `rollup` object     → pill from the canonical mapping (+ icons if `report`)
 *  - `rollup === null`   → the bulk lookup failed: "Unavailable" + Retry
 *  - `rollup` undefined  → payload predates the column: "—"
 *
 * Every pill and icon carries a tooltip and an accessible name; the pill's
 * tooltip states the meaning of the state, when the sync row was last
 * updated (with timezone) and, when the report is loaded, any inconsistency
 * between the rollup and the dimensions.
 *
 * @param {Object} props
 * @param {object | null | undefined} props.rollup `record.syncStatus`
 * @param {object | null} [props.report] per-user detail report, when loaded
 * @param {() => void} [props.onRetry] re-fetch when the rollup is unavailable
 */
export default function SyncStatusCell({ rollup, report = null, onRetry }) {
    const scopes = useScopes();

    if (rollup === undefined) {
        return (
            <div className={styles.cell}>
                <span className={`${styles.pill} ${styles.pill_neutral}`} aria-label="Sync status not loaded">
                    —
                </span>
            </div>
        );
    }

    if (rollup === null) {
        const unavailable = describeRollup(null);
        return (
            <div className={styles.cell}>
                <div className={styles.unavailableRow}>
                    <Tooltip title={unavailable.description} mouseEnterDelay={0.05}>
                        <span
                            className={`${styles.pill} ${styles[`pill_${unavailable.tone}`]}`}
                            role="status"
                            aria-label={`Sync status: ${unavailable.label}`}
                        >
                            <span className={styles.pillDot} />
                            {unavailable.label}
                        </span>
                    </Tooltip>
                    {typeof onRetry === 'function' ? (
                        <Button
                            variant={ButtonVariants.DEFAULT}
                            color={ButtonColors.SECONDARY}
                            onClick={(event) => { event.stopPropagation(); onRetry(); }}
                            className={styles.retryButton}
                            aria-label="Retry loading sync status"
                        >
                            Retry
                        </Button>
                    ) : null}
                </div>
            </div>
        );
    }

    const { rollup: state, dimensions, inconsistency } = describeSyncReport(report, { rollupFallback: rollup.status });
    const updated = rollup.updatedAt || rollup.createdAt;
    const updatedLabel = formatDateTime(updated);
    // Compare the two SERVER values (same vocabulary): the stored lifecycle
    // state vs the displayed rollup. Never compare against `state.key`, which
    // may be the loaded report's value or the synthetic UNKNOWN/UNAVAILABLE.
    const storedDiffers = Boolean(rollup.storedStatus && rollup.status && rollup.storedStatus !== rollup.status);
    const retryWindowExpired = Boolean(rollup.retryEligibleAt) && Date.parse(rollup.retryEligibleAt) <= Date.now();

    const pillTooltip = (
        <div className={styles.tooltipBody}>
            <div className={styles.tooltipTitle}>{state.label}</div>
            <div className={styles.tooltipNoteStrong}>{state.description}</div>
            {storedDiffers ? (
                <div className={styles.tooltipNote}>
                    Stored as {describeRollup(rollup.storedStatus).label}
                    {retryWindowExpired ? '; its retry window has expired.' : '.'}
                </div>
            ) : null}
            {updatedLabel ? (
                <div className={styles.tooltipRow}>
                    <span className={styles.tooltipKey}>Updated</span>
                    <span className={styles.tooltipVal}>{updatedLabel} {timeZoneAbbreviation(updated)}</span>
                </div>
            ) : null}
            {inconsistency ? <div className={styles.tooltipWarning}>{inconsistency}</div> : null}
        </div>
    );

    return (
        <div className={styles.cell}>
            {report ? (
                <div className={styles.dotsRow}>
                    {dimensions.filter((d) => d.state.applicable).map(({ key, label, Icon, requiredScope, entry, state: dimState }) => {
                        // Client-side only — the icon/state is never hidden,
                        // just the tooltip's detail rows when the viewer lacks
                        // this dimension's scope.
                        const canViewDetail = !requiredScope || scopes.includes(requiredScope);
                        const summary = canViewDetail && Array.isArray(entry?.summary) ? entry.summary : [];

                        const tooltipTitle = (
                            <div className={styles.tooltipBody}>
                                <div className={styles.tooltipTitle}>{label}</div>
                                <div className={styles.tooltipRow}>
                                    <span className={styles.tooltipKey}>Status</span>
                                    <span className={`${styles.tooltipVal} ${styles[`val_${dimState.tone}`]}`}>{dimState.label}</span>
                                </div>
                                <div className={styles.tooltipNote}>{dimState.description}</div>

                                {!canViewDetail && entry ? (
                                    <>
                                        <div className={styles.tooltipDivider} />
                                        <div className={styles.tooltipNote}>Restricted — you don&apos;t have permission to view this detail</div>
                                    </>
                                ) : null}

                                {summary.length > 0 ? (
                                    <>
                                        <div className={styles.tooltipDivider} />
                                        {summary.map((line, idx) => (
                                            <div key={`${line.label}-${idx}`} className={styles.tooltipRow}>
                                                <span className={styles.tooltipKey}>{line.label}</span>
                                                <span className={styles.tooltipVal}>{line.value}</span>
                                            </div>
                                        ))}
                                    </>
                                ) : null}

                                {entry?.error_message ? (
                                    <>
                                        <div className={styles.tooltipDivider} />
                                        <div className={styles.tooltipRow}>
                                            <span className={styles.tooltipKey}>Error</span>
                                            <span className={`${styles.tooltipVal} ${styles.val_error}`}>{entry.error_message}</span>
                                        </div>
                                    </>
                                ) : null}

                                {entry?.timestamp ? (
                                    <div className={styles.tooltipRow}>
                                        <span className={styles.tooltipKey}>When</span>
                                        <span className={styles.tooltipVal}>{formatDateTime(entry.timestamp)} {timeZoneAbbreviation(entry.timestamp)}</span>
                                    </div>
                                ) : null}

                                {entry && !dimState.required ? (
                                    <div className={styles.tooltipNote}>Not required for this user</div>
                                ) : null}
                            </div>
                        );

                        return (
                            <Tooltip key={key} title={tooltipTitle} mouseEnterDelay={0.05}>
                                <span
                                    className={[
                                        styles.dot,
                                        styles[`dot_${dimState.tone}`],
                                        dimState.required ? styles.dotRequired : styles.dotOptional,
                                    ].join(' ')}
                                    role="img"
                                    aria-label={`${label}: ${dimState.label}`}
                                >
                                    <Icon className={styles.dotIcon} />
                                </span>
                            </Tooltip>
                        );
                    })}
                </div>
            ) : null}

            <Tooltip title={pillTooltip} mouseEnterDelay={0.05}>
                <span
                    className={`${styles.pill} ${styles[`pill_${state.tone}`]} ${inconsistency ? styles.pillInconsistent : ''}`}
                    role="status"
                    aria-label={`Sync status: ${state.label}${inconsistency ? ' (inconsistent with dimensions)' : ''}`}
                >
                    <span className={styles.pillDot} />
                    {state.label}
                    {inconsistency ? <span className={styles.pillFlag} aria-hidden="true">!</span> : null}
                </span>
            </Tooltip>
        </div>
    );
}
